import { memo, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { isNil } from 'lodash';
// Import Types
import type { PlayersDataTableProps } from 'components/common/PlayersDataTable/PlayersDataTable';
// Import Hooks
import useRequest from 'hooks/UseRequest';
import useCancelableRequests from 'hooks/UseCancelableRequests';
// Import Services
import { CampaignHttpService } from 'services/http';
// Import Components
import PlayersDataTable from 'components/common/PlayersDataTable';
// Import Constants
import { CampaignActivityReceivedPlayersTableConfig } from './config';

const CampaignActivityMetricsReceivedPlayers = ({
    campaignId,
    startDate,
    endDate,
    setIsDisableDownload,
    isLoadingSchedule,
    isActive,
}) => {
    const { doPostRequest } = useRequest();
    const [isLoadingReceivedPlayers, setIsLoadingReceivedPlayers] = useState(true);
    const [isRequestBodyChanged, setIsRequestBodyChanged] = useState(false);
    const [receivedPlayers, setReceivedPlayers] = useState<PlayersDataTableProps['data']>(null);

    const { getCampaignEmailsReportRequest } = useCancelableRequests(
        { getCampaignEmailsReportRequest: CampaignHttpService.getCampaignEmailsReport },
        { takeLatest: ['getCampaignEmailsReportRequest'] },
    );

    useEffect(() => {
        if (!isLoadingSchedule && isActive && isRequestBodyChanged) {
            setIsLoadingReceivedPlayers(true);
            setIsRequestBodyChanged(false);

            doPostRequest(getCampaignEmailsReportRequest.request, {
                requestBody: { CampaignId: campaignId, StartDate: startDate, EndDate: endDate },
                successCallback: (Data) => {
                    setReceivedPlayers(Data);
                    setIsDisableDownload(isNil(Data?.Count) || Data?.Count === 0);
                },
            }).then(({ IsCanceled }) => {
                // A superseded call must not hide the loader of the call that replaced it
                if (!IsCanceled) {
                    setIsLoadingReceivedPlayers(false);
                }
            });
        }
    }, [
        isLoadingSchedule,
        isActive,
        isRequestBodyChanged,
        campaignId,
        startDate,
        endDate,
        setIsDisableDownload,
        getCampaignEmailsReportRequest,
        doPostRequest,
    ]);

    useEffect(() => {
        setIsRequestBodyChanged(true);
    }, [campaignId, startDate, endDate]);

    return (
        <PlayersDataTable
            key="ClientId"
            data={receivedPlayers}
            isLoading={isLoadingReceivedPlayers}
            config={CampaignActivityReceivedPlayersTableConfig}
        />
    );
};

CampaignActivityMetricsReceivedPlayers.propTypes = {
    campaignId: PropTypes.string.isRequired,
    startDate: PropTypes.string.isRequired,
    endDate: PropTypes.string.isRequired,
    setIsDisableDownload: PropTypes.func.isRequired,
    isLoadingSchedule: PropTypes.bool.isRequired,
    isActive: PropTypes.bool,
};

CampaignActivityMetricsReceivedPlayers.defaultProps = {
    isActive: true,
};

export default memo(CampaignActivityMetricsReceivedPlayers);
